/**
 * CognitiveAccountDropdown - Azure Cognitive Services account selector
 *
 * Fetches and displays Cognitive Service accounts (OpenAI, AIServices) for a subscription.
 * Auto-fetches endpoint when an account is selected.
 */

import { useMemo, useCallback, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Field,
  Button,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { ArrowSyncRegular, CheckmarkCircleFilled } from '@fluentui/react-icons';
import { SearchableCombobox, type SearchableComboboxOption } from '@/components/shared';
import { useCognitiveAccounts, useAccountCredentials } from '@/hooks/useAzureResources';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  combobox: {
    flex: 1,
    minWidth: 0,
  },
  refreshButton: {
    flexShrink: 0,
  },
  credentialsStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  credentialsLoading: {
    color: tokens.colorNeutralForeground3,
  },
  credentialsSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  credentialsError: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export interface CognitiveAccountDropdownProps {
  /** Subscription ID to filter accounts */
  subscriptionId: string;
  /** Currently selected account ID */
  value: string;
  /** Callback when selection changes */
  onChange: (accountId: string) => void;
  /** Callback when credentials are fetched */
  onCredentialsFetched?: (endpoint: string, key: string) => void;
  /** Field label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Validation error message */
  error?: string;
}

export const CognitiveAccountDropdown = ({
  subscriptionId,
  value,
  onChange,
  onCredentialsFetched,
  label = 'Azure OpenAI Resource',
  required = false,
  disabled = false,
  error,
}: CognitiveAccountDropdownProps) => {
  const styles = useStyles();

  const {
    data: accounts,
    isLoading,
    error: fetchError,
    refetch,
  } = useCognitiveAccounts(subscriptionId, !!subscriptionId);

  // Fetch credentials when account is selected
  const {
    endpoint,
    key,
    isLoading: isLoadingCredentials,
    error: credentialsError,
  } = useAccountCredentials(value, !!value);

  // Track if we've already notified for this endpoint/key combo to prevent infinite loops
  const notifiedCredentialsRef = useRef<string | null>(null);

  // Notify parent when credentials are fetched - use useEffect for side effects, not useMemo
  useEffect(() => {
    // Only notify if we have both endpoint and key, and haven't notified for this combo yet
    if (endpoint && key) {
      const credentialsKey = `${endpoint}:${key}`;
      if (credentialsKey !== notifiedCredentialsRef.current && onCredentialsFetched) {
        notifiedCredentialsRef.current = credentialsKey;
        onCredentialsFetched(endpoint, key);
      }
    }
  }, [endpoint, key, onCredentialsFetched]);

  // Reset the notification tracker when the account changes
  useEffect(() => {
    notifiedCredentialsRef.current = null;
  }, [value]);

  // Build options from accounts. Match against name + resource group + kind.
  const options = useMemo<SearchableComboboxOption[]>(() => {
    return (accounts ?? []).map((account) => ({
      value: account.id,
      text: account.name,
      secondaryText: `/${account.resourceGroup} • ${account.kind}`,
      searchTerms: [account.resourceGroup, account.kind, account.location],
    }));
  }, [accounts]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const isDisabled = disabled || !subscriptionId;
  const validationState = error || fetchError ? 'error' : 'none';
  const validationMessage = error ?? fetchError?.message ?? null;

  return (
    <div className={styles.container}>
      <Field
        label={label}
        required={required}
        validationState={validationState}
        validationMessage={validationMessage}
      >
        <div className={styles.fieldRow}>
          <SearchableCombobox
            className={styles.combobox}
            options={options}
            value={value}
            onChange={onChange}
            disabled={isDisabled || isLoading}
            placeholder={
              !subscriptionId
                ? 'Select a subscription first'
                : isLoading
                  ? 'Loading resources...'
                  : 'Select an Azure OpenAI resource'
            }
            aria-label={label}
          />
          <Button
            className={styles.refreshButton}
            appearance="subtle"
            icon={isLoading ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
            onClick={handleRefresh}
            disabled={isLoading || isDisabled}
            aria-label="Refresh resources"
            title="Refresh resources"
          />
        </div>
      </Field>

      {/* Credentials status */}
      {value && (
        <div className={styles.credentialsStatus}>
          {isLoadingCredentials && (
            <>
              <Spinner size="extra-tiny" />
              <Text className={styles.credentialsLoading}>Fetching endpoint and key...</Text>
            </>
          )}
          {!isLoadingCredentials && endpoint && key && (
            <>
              <CheckmarkCircleFilled className={styles.credentialsSuccess} />
              <Text className={styles.credentialsSuccess}>Endpoint and key retrieved</Text>
            </>
          )}
          {!isLoadingCredentials && credentialsError && (
            <Text className={styles.credentialsError}>
              Failed to fetch credentials: {credentialsError.message}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

CognitiveAccountDropdown.displayName = 'CognitiveAccountDropdown';
